import * as React from "react"
import {
  Focusable,
  OverlayArrow,
  Tooltip as TooltipPrimitive,
  TooltipTrigger as TooltipTriggerPrimitive,
} from "react-aria-components"

import { cn } from "@/lib/utils"

function TooltipTrigger({
  delay = 0,
  excludeFromTabOrder = false,
  focusable = true,
  children,
  ...props
}: React.ComponentProps<typeof TooltipTriggerPrimitive> & {
  /** Keep the trigger OUT of the tab order (tabIndex −1) — for DECORATIVE triggers (e.g. a count glyph) whose tooltip
   *  is a hover convenience, not keyboard-essential. The tooltip still shows on hover; keyboard/SR users must get the
   *  label another way (e.g. the trigger being a `role="img"` with an `aria-label`). Default `false` (interactive
   *  triggers stay focusable). */
  excludeFromTabOrder?: boolean
  /** `false` when the trigger is ALREADY a react-aria focusable (a `Button`, a select trigger …): those consume the
   *  tooltip's focusable context themselves, and the extra `Focusable` wrapper would only demand a DOM ref it cannot
   *  reach through a context-driven component (a console error per instance). Default `true` (plain DOM / custom
   *  triggers get the wrapper). */
  focusable?: boolean
}) {
  const [trigger, tooltip] = React.Children.toArray(children)

  return (
    <TooltipTriggerPrimitive
      data-slot="tooltip-trigger"
      delay={delay}
      {...props}
    >
      {focusable ? (
        <Focusable excludeFromTabOrder={excludeFromTabOrder}>
          {trigger as React.ComponentProps<typeof Focusable>["children"]}
        </Focusable>
      ) : (
        trigger
      )}
      {tooltip}
    </TooltipTriggerPrimitive>
  )
}

function Tooltip({
  className,
  placement = "top",
  offset = 4,
  crossOffset = 0,
  children,
  ...props
}: Omit<
  React.ComponentProps<typeof TooltipPrimitive>,
  "children" | "className"
> & {
  className?: string
  children?: React.ReactNode
}) {
  return (
    <TooltipPrimitive
      data-slot="tooltip-content"
      placement={placement}
      offset={offset}
      crossOffset={crossOffset}
      className={cn(
        "z-50 inline-flex w-fit max-w-xs origin-(--trigger-anchor-point) items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs text-background has-data-[slot=kbd]:pr-1.5 data-entering:animate-in data-entering:fade-in-0 data-entering:zoom-in-95 data-exiting:animate-out data-exiting:fade-out-0 data-exiting:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm",
        className
      )}
      {...props}
    >
      {children}
      <OverlayArrow
        className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground"
        style={({ placement, defaultStyle }) => ({
          ...defaultStyle,
          rotate: "0deg",
          translate: "0 0",
          transform:
            placement === "bottom"
              ? "translate(-50%, calc(50% + 2px)) rotate(45deg)"
              : placement === "top"
                ? "translate(-50%, calc(-50% - 2px)) rotate(45deg)"
                : placement === "left"
                  ? "translate(calc(-50% - 2px), -50%) rotate(45deg)"
                  : "translate(calc(50% + 2px), -50%) rotate(45deg)",
        })}
      />
    </TooltipPrimitive>
  )
}

export { Tooltip, TooltipTrigger }
